/* istanbul ignore file */
import React from 'react';
import { GetServerSidePropsContext } from 'next';

import LoginRegisterModalProvider
  from '@components/shared/providers/LoginRegisterModalProvider/LoginRegisterModalProvider';
import AddToBoardModalProvider
  from '@components/shared/providers/AddToBoardModalProvider/AddToBoardModalProvider';
import AddedToCartConfirmationModalProvider from '@components/shared/providers/AddedToCartConfirmationModalProvider/AddedToCartConfirmationModalProvider';
import PRODUCT_QUERY from '@graphql/VendorVariant/queries/get-product.graphql';
import {
  COMPLIMENTARY_PRODUCTS,
  SIMILAR_PRODUCTS_PDP,
} from '@graphql/VendorVariant/queries/get-similar-products.graphql';
import { withAuthenticatedUser } from '@lib/user/auth';
import { getApollo } from '@lib/apollo/apolloClient';
import PageProductV2 from '@components/pages/ProductV2/ProductDetails/PageProducts';
import { stringify as urlQueryStringify } from 'querystring';
import getProductVanityPath from '@lib/vendorVariant/getProductVanityPath';
import logger from '@lib/logger';
import isDoNotUseCommissionTier from '@lib/vendorVariant/isDoNotUseCommissionTier';
import {
  IVendorVariantVisibilityType as visibilityType,
} from '@models/VendorVariant/i-vendor-variant';
import { IAuthResponse } from '@models/shared/i-auth-response';
import { allFulfilled } from '@lib/promise';
import { ApolloClient } from '@apollo/client';
import { fetchShopNavigationMenu } from '@components/shared/shop/ShopNavigationMenu';
import env from '@lib/envs/env';
import browser from '@utils/browser/browser';
import ShareModalProvider from '@components/shared/providers/ShareModalProvider/ShareModalProvider';
import Compose from '@components/shared/Compose';
import { userIsAdmin } from '@lib/auth/auth';
import { notFound } from '@utils/navigation/serverSideHelpers';
import { getResizedImageUrl } from '@lib/image';
import { NoIndexVendorsList } from '@components/shared/shop/model/Vendors';
import isAuthedOnlyProduct from '@components/pages/ProductV2/shared/utils/productUtils';

const ProductDetails = (props: any) => (
  <Compose
    components={[
      LoginRegisterModalProvider,
      ShareModalProvider,
      AddToBoardModalProvider,
      AddedToCartConfirmationModalProvider
    ]}
  >
    <PageProductV2 {...props} />
  </Compose>
);

export default ProductDetails;

export const getServerSideProps = withAuthenticatedUser(async ({
  user,
}, ctx: GetServerSidePropsContext) => {
  const log = logger('PDP');

  const { query: { id }, req } = ctx;

  const userAgent = req.headers['user-agent'];
  const isIosNative = browser.isIosNativeApp(userAgent || '');
  const productId = parseInt((id || '').toString().split('-').pop() as string, 10);
  const apolloClient = getApollo();

  return getServerSidePropsV2(
    apolloClient,
    productId,
    id,
    user,
    ctx,
    log,
    isIosNative,
  );
}, {
  includeExperimentVariations: true,
  requireAuth: false,
});

function isNoIndex(
  host: string,
  ctx: any,
  vendorVariant: any
) {
  if (NoIndexVendorsList.includes(Number(vendorVariant.vendor?.id))) {
    return true;
  }

  const isNotVisibleInShop = !vendorVariant.visibilities?.includes(
    visibilityType.VISIBLE_IN_SHOP
  );

  const { resolvedUrl } = ctx;
  const { productCanonicalUrl } = vendorVariant;

  const isNotCanonicalUrl = `${host}${resolvedUrl.split('?')[0]}` !== productCanonicalUrl;

  return isNotCanonicalUrl || isNotVisibleInShop;
}

// if commission tier is not available, or is DNU
// vendor_variant_visibility should not be added to this until we think through
// there are many products (~513k) that are visible for designers but not for customers
function isHiddenProduct(
  vendorVariant: any
): boolean {
  const commissionTierId = vendorVariant.commissionTier?.id;
  const vendorDoNotUse = vendorVariant.vendor?.doNotUse;
  return !commissionTierId ||
    isDoNotUseCommissionTier(commissionTierId) ||
    vendorDoNotUse;
}

async function getServerSidePropsV2(
  apolloClient: ApolloClient<any>,
  productId: number,
  id: string | string[] | undefined,
  user: IAuthResponse | null,
  ctx: GetServerSidePropsContext,
  log: any,
  isIosNative: boolean,
) {
  const [
    vendorVariantIdResponse,
    similarProductsResponse,
    relatedAssortmentProducts
  ] = await allFulfilled([
    apolloClient.query({
      query: PRODUCT_QUERY,
      variables: {
        productId
      },
    }),
    apolloClient.query({
      query: SIMILAR_PRODUCTS_PDP,
      variables: {
        id: productId,
        pageSize: 15,
      }
    }),
    apolloClient.query({
      query: COMPLIMENTARY_PRODUCTS,
      variables: {
        vendorVariantId: productId,
      }
    })
  ]);

  if (!vendorVariantIdResponse || !vendorVariantIdResponse.data?.vendorVariantById) {
    return notFound;
  }

  const { data: { vendorVariantById: vendorVariant } } = vendorVariantIdResponse;
  const { data: similarData } = similarProductsResponse || { data: {} };
  const { data: { complimentaryBoardProducts } } = relatedAssortmentProducts || { data: {} };

  const completeTheLookData = complimentaryBoardProducts?.map((vv: any) => vv.vendorVariant) ?? [];

  const userIsAnnonymous = !user;

  const isHidden = isHiddenProduct(vendorVariant);

  if (!userIsAdmin(user) && isHidden) {
    const query = {
      ismissing: 1,
      missingproduct: vendorVariant.title,
      missingvendor: vendorVariant.vendor.name,
    };
    const destination = `/shop/${
      vendorVariant.taxonomy.preferredTaxonomyCategory?.parentTaxonomyCategory?.slug || ''
    }`;

    return {
      redirect: {
        permanent: true,
        destination: `${destination}?${urlQueryStringify(query)}`,
      },
    };
  }

  // Redirect if user is anonymous and the product is logged in access only
  if (userIsAnnonymous && isAuthedOnlyProduct(vendorVariant)) {
    return {
      redirect: {
        destination: `/shop`,
        statusCode: 302,
      },
    };
  }

  const vanityPath = getProductVanityPath(vendorVariant);
  const vanityId = vanityPath.split('/').slice(-1)[0];

  if (vanityId !== id) {
    log.debug({ vanityId }, 'Redirecting to vanity URL');
    return {
      redirect: {
        destination: vanityPath,
        statusCode: 301,
      },
    };
  }

  const { productCanonicalUrl } = vendorVariant;

  const host = `${env.IS_LOCAL ? 'http' : 'https'}://${env.DOMAIN_NAME}`;

  const noIndex = isNoIndex(
    host,
    ctx,
    vendorVariant
  );

  const shopNavigationMenu = await fetchShopNavigationMenu({
    previewData: ctx.previewData,
    apolloClient,
  });

  const collectionWithProducts = {
    ctaLabel: 'Shop Now',
    image: {
      alt: 'Best Sellers for a Reason product collection',
      url: getResizedImageUrl({ url: 'https://s3.us-east-1.amazonaws.com/static.havenly.com/product/production/list_page-best_sellers.jpg' }),
    },
    ctaUrl: '/shop/collection/best-sellers-for-a-reason',
    imageTitle: 'Best Sellers for a Reason'
  };

  return {
    props: {
      user,
      isHidden,
      isIosNative,
      vendorVariant,
      similarData,
      completeTheLookData,
      host,
      canonicalUrl: productCanonicalUrl,
      noIndex,
      shopNavigationMenu,
      collectionWithProducts,
    }
  };
}
